import React from 'react';
import {
  CheckCircle2, Loader2, PauseCircle, XCircle, Cpu, Zap, DollarSign, Clock, FileText,
} from 'lucide-react';
import { FeedItem } from '../../hooks/useRunEvents';

function dotFor(item: FeedItem) {
  if (item.type === 'telemetry') return <Cpu size={14} color="var(--mx-text-subtle)" />;
  if (item.type === 'gate') return <PauseCircle size={14} color="var(--mx-warning)" />;
  switch (item.status) {
    case 'succeeded': return <CheckCircle2 size={14} color="var(--mx-success)" />;
    case 'running': return <Loader2 size={14} color="var(--mx-accent)" className="mx-spin" />;
    case 'failed':
    case 'cancelled': return <XCircle size={14} color="var(--mx-danger)" />;
    default: return <FileText size={14} color="var(--mx-text-subtle)" />;
  }
}

function labelFor(item: FeedItem): string {
  if (item.message) return item.message;
  if (item.stage) return `${item.stage}${item.stream ? ` · ${item.stream}` : ''} ${item.status || ''}`;
  return item.type;
}

const time = (ts?: string) => (ts ? new Date(ts).toLocaleTimeString([], { hour12: false }) : '');

const Chips: React.FC<{ m: any }> = ({ m }) => {
  if (!m) return null;
  const chips: React.ReactNode[] = [];
  if (m.latency_ms) chips.push(<span key="l" className="mx-chip"><Clock size={11} />{(m.latency_ms / 1000).toFixed(1)}s</span>);
  if (m.cost_usd) chips.push(<span key="c" className="mx-chip"><DollarSign size={11} />{m.cost_usd.toFixed(4)}</span>);
  if (m.energy_joules) chips.push(<span key="e" className="mx-chip"><Zap size={11} />{Math.round(m.energy_joules)}J</span>);
  if (m.tokens_out) chips.push(<span key="t" className="mx-chip">{m.tokens_in || 0}/{m.tokens_out} tok</span>);
  if (!chips.length) return null;
  return <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>{chips}</div>;
};

const EventTimeline: React.FC<{ feed: FeedItem[]; live?: boolean }> = ({ feed, live }) => {
  if (!feed.length) {
    return <div className="mx-muted" style={{ fontSize: 13, padding: '8px 0' }}>
      {live ? 'Waiting for events…' : 'No events recorded.'}
    </div>;
  }
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', left: 7, top: 6, bottom: 6, width: 2,
        background: 'var(--mx-border)' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {feed.map((item, i) => (
          <div key={item.seq ?? i} style={{ display: 'flex', gap: 12, position: 'relative' }}>
            <div style={{ zIndex: 1, background: 'var(--mx-surface)', width: 16, height: 16,
              display: 'grid', placeItems: 'center', marginTop: 1 }}>{dotFor(item)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span className="mx-subtle mx-mono" style={{ fontSize: 11 }}>{time(item.ts)}</span>
                <span style={{ fontSize: 13.5, color: 'var(--mx-text)' }}>{labelFor(item)}</span>
              </div>
              {item.type === 'telemetry' && <Chips m={item.metrics} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventTimeline;
